import Base from '../base/base';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import './image-wipe-slider.scss';

gsap.registerPlugin(CustomEase);

interface ImageWipeSliderOptions {
  selector: string;
  viewportSelector?: string;
  slideSelector?: string;
  previewSelector?: string;
  progressSelector?: string;
  showCredits?: boolean;
  initialAutoplayDelay?: number;
  autoplayDelay?: number;
  transitionDuration?: number;
  ease?: string;
  waitUntilVisible?: boolean;
  debug?: boolean;
}

export default class ImageWipeSlider extends Base {
  private config: Required<Omit<ImageWipeSliderOptions, 'debug'>>;
  private wrapper: HTMLElement | null = null;
  private viewport: HTMLElement | null = null;
  private slides: HTMLElement[] = [];
  private previews: HTMLElement[] = [];
  private progressBars: HTMLElement[] = [];
  private creditPill: HTMLElement | null = null;
  private currentIndex = 0;
  private isAnimating = false;
  private autoplayTimer: ReturnType<typeof setTimeout> | null = null;
  private initialDelayTimer: ReturnType<typeof setTimeout> | null = null;
  private observer: IntersectionObserver | null = null;
  private hasStarted = false;

  constructor(options: ImageWipeSliderOptions) {
    super(options.debug || false);

    this.config = {
      selector: options.selector,
      viewportSelector: options.viewportSelector ?? '.ic-image-wrap',
      slideSelector: options.slideSelector ?? '.gallery-collection .image-collection-item',
      previewSelector: options.previewSelector ?? '.slider-preview .preview',
      progressSelector: options.progressSelector ?? '.progress',
      showCredits: options.showCredits ?? false,
      initialAutoplayDelay: options.initialAutoplayDelay ?? 800,
      autoplayDelay: options.autoplayDelay ?? 4000,
      transitionDuration: options.transitionDuration ?? 1.5,
      ease: options.ease ?? 'hop',
      waitUntilVisible: options.waitUntilVisible ?? true
    };

    if (this.config.ease === 'hop') {
      CustomEase.create('hop', 'M0,0 C0.071,0.505 0.192,0.726 0.318,0.852 0.45,0.984 0.504,1 1,1');
    }

    this.init();
  }

  init(): void {
    this.wrapper = document.querySelector<HTMLElement>(this.config.selector);
    if (!this.wrapper) {
      this.log('ImageWipeSlider wrapper not found:', this.config.selector);
      return;
    }

    this.viewport = this.wrapper.querySelector<HTMLElement>(this.config.viewportSelector) || this.wrapper;
    this.slides = Array.from(this.wrapper.querySelectorAll<HTMLElement>(this.config.slideSelector));

    if (!this.slides.length) {
      this.log('No slides found for selector:', this.config.slideSelector);
      return;
    }

    this.previews = Array.from(this.wrapper.querySelectorAll<HTMLElement>(this.config.previewSelector));
    this.progressBars = this.previews
      .map((preview) => preview.querySelector<HTMLElement>(this.config.progressSelector))
      .filter((el): el is HTMLElement => !!el);

    this.prepareLayout();
    this.bindPreviewEvents();
    this.syncPreviewState();
    this.initCredits();

    if (this.slides.length === 1) {
      this.log('Single slide found, autoplay disabled.');
      return;
    }

    if (this.config.waitUntilVisible) {
      this.observeVisibility();
      return;
    }

    this.startAutoplay();
  }

  private prepareLayout(): void {
    if (!this.viewport) return;

    this.viewport.style.position = this.viewport.style.position || 'relative';
    this.viewport.style.overflow = 'hidden';

    this.slides.forEach((slide, index) => {
      slide.style.position = 'absolute';
      slide.style.inset = '0';
      slide.style.width = '100%';
      slide.style.height = '100%';

      gsap.set(slide, {
        zIndex: index === this.currentIndex ? 2 : 0,
        clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
      });
    });
  }

  private observeVisibility(): void {
    if (!this.wrapper) return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      });
    });

    this.observer.observe(this.wrapper);
  }

  private startAutoplay(): void {
    if (this.hasStarted) {
      this.scheduleNext();
      return;
    }

    this.hasStarted = true;
    this.stopAutoplay();
    this.initialDelayTimer = setTimeout(() => {
      this.scheduleNext();
    }, this.config.initialAutoplayDelay);
  }

  private scheduleNext(): void {
    this.stopAutoplay();
    this.animateProgress();
    this.autoplayTimer = setTimeout(() => {
      this.goTo((this.currentIndex + 1) % this.slides.length, 'right');
    }, this.config.autoplayDelay);
  }

  private stopAutoplay(): void {
    if (this.initialDelayTimer) {
      clearTimeout(this.initialDelayTimer);
      this.initialDelayTimer = null;
    }

    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    this.progressBars.forEach((bar) => {
      gsap.killTweensOf(bar, { width: true });
      bar.style.width = '0%';
    });
  }

  private bindPreviewEvents(): void {
    if (!this.previews.length) return;

    this.previews.forEach((preview, index) => {
      preview.addEventListener('click', (event) => {
        event.preventDefault();
        if (index === this.currentIndex) return;
        const direction = index > this.currentIndex ? 'right' : 'left';
        this.goTo(index, direction);
      });
    });
  }

  private goTo(index: number, direction: 'left' | 'right'): void {
    if (this.isAnimating || index === this.currentIndex) {
      return;
    }

    const outgoingSlide = this.slides[this.currentIndex];
    const incomingSlide = this.slides[index];

    if (!outgoingSlide || !incomingSlide) {
      return;
    }

    this.isAnimating = true;
    this.stopAutoplay();
    this.animateCreditOut();

    gsap.set(incomingSlide, {
      zIndex: 1,
      clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
    });

    gsap.fromTo(
      outgoingSlide,
      { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' },
      {
        clipPath: direction === 'right'
          ? 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)'
          : 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)',
        duration: this.config.transitionDuration,
        ease: this.config.ease,
        onComplete: () => {
          gsap.set(outgoingSlide, { zIndex: 0, clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' });
          gsap.set(incomingSlide, { zIndex: 2 });

          this.currentIndex = index;
          this.isAnimating = false;
          this.syncPreviewState();
          this.syncCredit(true);
          this.scheduleNext();
        }
      }
    );
  }

  private initCredits(): void {
    if (!this.config.showCredits || !this.viewport) {
      return;
    }

    this.creditPill = document.createElement('div');
    this.creditPill.className = 'iws-credit-pill';
    this.creditPill.style.display = 'none';
    this.viewport.appendChild(this.creditPill);

    this.syncCredit(false);
  }

  private getCurrentCreditText(): string {
    const activeSlide = this.slides[this.currentIndex];
    const image = activeSlide?.querySelector<HTMLImageElement>('img');
    return (image?.alt || '').trim();
  }

  private syncCredit(animateIn: boolean): void {
    if (!this.creditPill || !this.config.showCredits) {
      return;
    }

    const creditText = this.getCurrentCreditText();

    if (!creditText) {
      this.creditPill.textContent = '';
      this.creditPill.style.display = 'none';
      gsap.set(this.creditPill, { opacity: 0, y: 8, clearProps: 'willChange' });
      return;
    }

    this.creditPill.textContent = creditText;
    this.creditPill.style.display = 'inline-flex';

    if (!animateIn) {
      gsap.set(this.creditPill, { opacity: 1, y: 0, clearProps: 'willChange' });
      return;
    }

    gsap.fromTo(
      this.creditPill,
      { opacity: 0, y: 8, willChange: 'transform,opacity' },
      { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out', clearProps: 'willChange' }
    );
  }

  private animateCreditOut(): void {
    if (!this.creditPill || this.creditPill.style.display === 'none') {
      return;
    }

    gsap.to(this.creditPill, {
      opacity: 0,
      y: 8,
      duration: 0.35,
      ease: 'power2.in',
      overwrite: true
    });
  }

  private syncPreviewState(): void {
    if (!this.previews.length) return;

    this.previews.forEach((preview, index) => {
      preview.classList.toggle('active', index === this.currentIndex);
    });
  }

  private animateProgress(): void {
    if (!this.progressBars.length) return;

    this.progressBars.forEach((bar) => {
      gsap.killTweensOf(bar, { width: true });
      bar.style.width = '0%';
    });

    const activeBar = this.progressBars[this.currentIndex];
    if (!activeBar) return;

    gsap.fromTo(activeBar, { width: '0%' }, { width: '100%', duration: this.config.autoplayDelay / 1000, ease: 'none' });
  }

  destroy(): void {
    this.stopAutoplay();
    if (this.observer && this.wrapper) {
      this.observer.unobserve(this.wrapper);
    }
    this.observer = null;

    if (this.creditPill) {
      this.creditPill.remove();
      this.creditPill = null;
    }
  }
}
